import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../../services/firebase';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  TextField,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  PhoneAndroid as PhoneIcon,
  ArrowForward as ArrowForwardIcon,
  MarkEmailRead as VerifiedIcon
} from '@mui/icons-material';

export default function VerifyEmail() {
  const { currentUser, logout, reloadUser, updateUserEmail } = useAuth();
  const navigate = useNavigate();

  // Active user data
  const userEmail = currentUser?.email || auth.currentUser?.email || '';
  const userPhone = currentUser?.phoneNumber || auth.currentUser?.phoneNumber || '';
  const isPhoneOnly = !userEmail && Boolean(userPhone);

  // States
  const [checking, setChecking] = useState(false); // Manual check indicator
  const [backgroundChecking, setBackgroundChecking] = useState(false); // Silent background polling
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Resend countdown: available immediately on initial load!
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Edit / Add email dialog states
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState(userEmail);
  const [savingEmail, setSavingEmail] = useState(false);

  const checkingLock = useRef(false);
  const redirectedRef = useRef(false);

  // Verification Checker
  const checkVerification = async (isManual = false) => {
    if (checkingLock.current || redirectedRef.current) return false;
    checkingLock.current = true;

    if (isManual) {
      setChecking(true);
      setError('');
    } else {
      setBackgroundChecking(true);
    }

    try {
      let updatedUser = null;
      if (typeof reloadUser === 'function') {
        updatedUser = await reloadUser();
      } else if (auth.currentUser) {
        await auth.currentUser.reload();
      }

      const isVerified = Boolean(
        updatedUser?.emailVerified ||
        auth.currentUser?.emailVerified ||
        currentUser?.emailVerified
      );

      if (isVerified) {
        redirectedRef.current = true;
        setSuccess('Email verified successfully! Redirecting...');
        setTimeout(() => {
          navigate('/profile', { replace: true });
        }, 1000);
        return true;
      } else if (isManual) {
        setError('Your email is not verified yet. Please click the link in your inbox and try again.');
      }
    } catch (err) {
      console.error('Error checking verification:', err);
      if (isManual) {
        setError('Could not verify status. Please try clicking the button again.');
      }
    } finally {
      checkingLock.current = false;
      if (isManual) setChecking(false);
      setBackgroundChecking(false);
    }
    return false;
  };

  // Immediate check on mount
  useEffect(() => {
    // If already verified, go straight to profile
    if (auth.currentUser?.emailVerified || currentUser?.emailVerified) {
      redirectedRef.current = true;
      navigate('/profile', { replace: true });
      return;
    }

    // If phone-only user with no email, do not block or trap
    if (isPhoneOnly) {
      return;
    }

    checkVerification(false);
  }, [currentUser?.emailVerified, isPhoneOnly]);

  // Periodic silent polling in background every 8 seconds (only if user has an email)
  useEffect(() => {
    if (isPhoneOnly || !userEmail) return;

    const interval = setInterval(() => {
      checkVerification(false);
    }, 8000);

    return () => clearInterval(interval);
  }, [userEmail, isPhoneOnly]);

  // Auto-check when switching back from email tab
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && !isPhoneOnly) {
        checkVerification(false);
      }
    };

    window.addEventListener('focus', handleVisibility);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleVisibility);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isPhoneOnly]);

  // Countdown timer for resend button cooldown (only runs after user clicks Resend)
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Handle Resend Verification Email
  const handleResend = async () => {
    const activeEmail = (userEmail || newEmailInput || '').trim();
    if (!activeEmail) {
      setEditEmailOpen(true);
      return;
    }

    const activeUser = auth.currentUser || currentUser;
    if (!canResend || !activeUser) return;

    try {
      setResending(true);
      setError('');

      try {
        const resp = await fetch('https://admin.verveailabs.com/api/auth/send-custom-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'EMAIL_VERIFICATION',
            email: activeEmail,
            recipientName: activeUser.displayName || '',
            redirectUrl: `${window.location.origin}/profile`
          })
        });
        if (!resp.ok) throw new Error('API status ' + resp.status);
      } catch (backendErr) {
        console.warn('Backend custom verification resend notice, using Firebase SDK fallback:', backendErr);
        const actionCodeSettings = {
          url: `${window.location.origin}/profile`,
          handleCodeInApp: false
        };
        const fbUser = auth.currentUser || (currentUser && typeof currentUser.getIdToken === 'function' ? currentUser : null);
        if (fbUser) {
          await sendEmailVerification(fbUser, actionCodeSettings);
        } else {
          throw backendErr;
        }
      }

      setSuccess(`Verification email sent to ${activeEmail}! Please check your inbox and spam folder.`);
      setCountdown(45); // 45s cooldown
      setCanResend(false);
    } catch (err) {
      console.error('Error sending verification email:', err);
      if (err.code === 'auth/too-many-requests') {
        setError('Too many requests. Please wait a minute before trying again.');
      } else {
        setError('Failed to send verification email. Please check your email address and try again.');
      }
    } finally {
      setResending(false);
    }
  };

  // Handle Edit / Add Email Submit
  const handleSaveEmail = async () => {
    const clean = (newEmailInput || '').trim().toLowerCase();
    if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSavingEmail(true);
    setError('');
    try {
      if (typeof updateUserEmail === 'function') {
        await updateUserEmail(clean);
      }

      setEditEmailOpen(false);
      setSuccess(`Email updated to ${clean}. Verification link is ready to be sent!`);
      setCanResend(true);
      setCountdown(0);
    } catch (err) {
      console.error('Error updating email:', err);
      setError(err.message || 'Failed to update email.');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess('');
    setError('');
  };

  // If completely unauthenticated, redirect to login
  if (!currentUser && !auth.currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: { xs: 6, md: 8 },
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            padding: { xs: 3, sm: 5 },
            width: '100%',
            textAlign: 'center',
            bgcolor: '#0e131f',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 4,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Subtle top glow accent */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '20%',
              right: '20%',
              height: '2px',
              background: 'linear-gradient(90deg, transparent, #00F5FF, transparent)',
            }}
          />

          {/* Icon */}
          <Box sx={{ mb: 2.5, mt: 1 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                bgcolor: 'rgba(0, 245, 255, 0.1)',
                border: '1px solid rgba(0, 245, 255, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                boxShadow: '0 0 24px rgba(0, 245, 255, 0.2)'
              }}
            >
              <EmailIcon sx={{ fontSize: 36, color: '#00F5FF' }} />
            </Box>
          </Box>

          {/* Title */}
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#F8FAFC', mb: 1, fontSize: { xs: '1.75rem', sm: '2rem' } }}>
            Verify Your Email
          </Typography>

          {/* Target Email or Phone User Info */}
          {userEmail ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: '#94A3B8', mb: 1 }}>
                We've sent a verification link to:
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  px: 2,
                  py: 0.8,
                  borderRadius: 3
                }}
              >
                <Typography variant="subtitle1" sx={{ color: '#00F5FF', fontWeight: 700, fontFamily: 'monospace' }}>
                  {userEmail}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setNewEmailInput(userEmail);
                    setEditEmailOpen(true);
                  }}
                  title="Change or correct email"
                  sx={{ color: '#94A3B8', '&:hover': { color: '#00F5FF' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            /* Phone Authenticated User with No Email */
            <Box sx={{ mb: 3 }}>
              <Alert
                severity="info"
                icon={<PhoneIcon sx={{ color: '#38bdf8' }} />}
                sx={{
                  bgcolor: 'rgba(56, 189, 248, 0.08)',
                  border: '1px solid rgba(56, 189, 248, 0.2)',
                  color: '#e0f2fe',
                  textAlign: 'left',
                  mb: 2,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Logged in via mobile: {userPhone || 'Verified Founder'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#bae6fd', display: 'block' }}>
                  Your phone is verified. You can link a business email now or continue directly to your profile.
                </Typography>
              </Alert>

              <Button
                variant="outlined"
                size="small"
                startIcon={<EmailIcon />}
                onClick={() => setEditEmailOpen(true)}
                sx={{
                  borderColor: 'rgba(0, 245, 255, 0.4)',
                  color: '#00F5FF',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  mb: 1.5,
                  '&:hover': {
                    borderColor: '#00F5FF',
                    bgcolor: 'rgba(0, 245, 255, 0.08)'
                  }
                }}
              >
                Add Business Email Address
              </Button>
            </Box>
          )}

          {/* Instructions Box */}
          <Alert
            severity="info"
            sx={{
              mb: 3,
              textAlign: 'left',
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              color: '#cbd5e1',
              borderRadius: 2.5
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 0.8 }}>
              Check your inbox and click the verification link
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1.6, display: 'block' }}>
              • Check your spam or promotions folder if you don't see it<br />
              • The link is valid for 24 hours<br />
              • Once clicked in your email, click the confirmation button below
            </Typography>
          </Alert>

          {/* Background Checking Status Indicator */}
          {backgroundChecking && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 1 }}>
              <CircularProgress size={14} sx={{ color: '#00F5FF' }} />
              <Typography variant="caption" sx={{ color: '#64748B' }}>
                Listening for verification confirmation...
              </Typography>
            </Box>
          )}

          {/* Primary Action Button */}
          <Button
            variant="contained"
            size="large"
            onClick={() => checkVerification(true)}
            disabled={checking}
            startIcon={checking ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            fullWidth
            sx={{
              mb: 2,
              py: 1.4,
              fontWeight: 700,
              fontSize: '0.98rem',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #00F5FF 0%, #00C8FF 100%)',
              color: '#0A0E17',
              borderRadius: 2.5,
              boxShadow: '0 4px 14px rgba(0, 245, 255, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #00E5EF 0%, #00B8EF 100%)',
                boxShadow: '0 6px 20px rgba(0, 245, 255, 0.45)'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.4)'
              }
            }}
          >
            {checking ? 'Checking Status...' : "I've Verified My Email — Continue"}
          </Button>

          {/* Resend Action Button (Always visible & available) */}
          <Button
            variant="outlined"
            startIcon={resending ? <CircularProgress size={18} sx={{ color: '#00F5FF' }} /> : <RefreshIcon />}
            onClick={handleResend}
            disabled={!canResend || resending}
            fullWidth
            sx={{
              mb: 2,
              py: 1.2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.92rem',
              borderRadius: 2.5,
              borderColor: canResend ? 'rgba(0, 245, 255, 0.4)' : 'rgba(255, 255, 255, 0.1)',
              color: canResend ? '#00F5FF' : '#64748B',
              '&:hover': {
                borderColor: '#00F5FF',
                bgcolor: 'rgba(0, 245, 255, 0.08)'
              }
            }}
          >
            {resending
              ? 'Sending Verification Link...'
              : canResend
              ? 'Resend Verification Email'
              : `Resend Available in ${countdown}s`}
          </Button>

          {/* Alternative: Phone / Profile Direct Bypass */}
          {isPhoneOnly && (
            <Button
              variant="text"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/profile', { replace: true })}
              fullWidth
              sx={{
                mb: 1.5,
                color: '#38bdf8',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.9rem'
              }}
            >
              Continue to Profile with Verified Mobile Number
            </Button>
          )}

          {/* Edit Email Link */}
          {userEmail && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setNewEmailInput(userEmail);
                  setEditEmailOpen(true);
                }}
                sx={{
                  color: '#94a3b8',
                  textTransform: 'none',
                  fontSize: '0.82rem',
                  '&:hover': { color: '#00F5FF' }
                }}
              >
                Wrong email address? Click to change or correct it
              </Button>
            </Box>
          )}

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

          {/* Footer Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/')}
              sx={{ color: '#64748B', textTransform: 'none', fontSize: '0.8rem', '&:hover': { color: '#94A3B8' } }}
            >
              &larr; Back to Home
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={handleLogout}
              sx={{ color: '#ef4444', textTransform: 'none', fontSize: '0.8rem', '&:hover': { color: '#f87171' } }}
            >
              Sign Out
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Edit / Add Email Modal */}
      <Dialog
        open={editEmailOpen}
        onClose={() => setEditEmailOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0e131f',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 3,
            color: '#fff',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#F8FAFC' }}>
          {userEmail ? 'Update Verification Email' : 'Add Business Email'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#94A3B8', mb: 2.5 }}>
            {userEmail
              ? 'Enter the correct email address. We will update your profile and send a new verification link.'
              : 'Add your business email to receive project updates, deliverables, and official communications.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Email Address"
            placeholder="founder@yourcompany.com"
            value={newEmailInput}
            onChange={(e) => setNewEmailInput(e.target.value)}
            disabled={savingEmail}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                bgcolor: 'rgba(255, 255, 255, 0.04)',
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#00F5FF' },
                '&.Mui-focused fieldset': { borderColor: '#00F5FF' }
              },
              '& .MuiInputLabel-root': { color: '#94A3B8' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#00F5FF' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setEditEmailOpen(false)}
            disabled={savingEmail}
            sx={{ color: '#94A3B8', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEmail}
            disabled={savingEmail || !newEmailInput.trim()}
            startIcon={savingEmail ? <CircularProgress size={16} color="inherit" /> : <VerifiedIcon />}
            sx={{
              background: 'linear-gradient(135deg, #00F5FF 0%, #00C8FF 100%)',
              color: '#0A0E17',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2
            }}
          >
            {savingEmail ? 'Saving...' : 'Save & Send Link'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          variant="filled"
          icon={<CheckCircleIcon />}
          sx={{ width: '100%', bgcolor: '#059669', color: '#fff' }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          variant="filled"
          sx={{ width: '100%', bgcolor: '#dc2626', color: '#fff' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
